import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { PopupModal } from "../../components/TopBar/PopupModal/PopupModal";
import { MAIN_MENU_WIDTH, SUB_MENU_WIDTH } from "../layoutConstants";

/**
 * @param {object} props
 * @param {() => void} props.closeSubMenu
 * @param {import("../../navigation/navigationCatalog").SidebarChild[]} props.subMenuItems
 * @param {string} props.moduleLabel
 * @param {string | null} props.activeChildKey
 * @param {string} props.panelId
 * @param {import("react").MutableRefObject<Record<string, HTMLElement | null>>} [props.moduleTriggerRefs]
 * @param {string} [props.returnFocusKey]
 */
export function SubMenu({
  closeSubMenu,
  subMenuItems,
  moduleLabel,
  activeChildKey,
  panelId,
  moduleTriggerRefs,
  returnFocusKey,
}) {
  const [filterQuery, setFilterQuery] = useState("");
  const searchRef = useRef(null);

  const filteredItems = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) {
      return subMenuItems;
    }
    return subMenuItems.filter(
      item =>
        item.name.toLowerCase().includes(q) || (item.group?.toLowerCase().includes(q) ?? false),
    );
  }, [filterQuery, subMenuItems]);

  useEffect(() => {
    setFilterQuery("");
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [moduleLabel, subMenuItems]);

  const handleClose = () => {
    closeSubMenu();
    if (returnFocusKey && moduleTriggerRefs?.current) {
      window.setTimeout(() => moduleTriggerRefs.current[returnFocusKey]?.focus(), 0);
    }
  };

  return (
    <PopupModal closeModal={handleClose} closeOnEscape>
      <SubMenuNav
        id={panelId}
        className="subMenu"
        role="navigation"
        aria-label={`${moduleLabel} pages`}
      >
        <div className="subMenuHeader">
          <span className="moduleTitle">{moduleLabel}</span>
          <button type="button" className="closeButton" onClick={handleClose} aria-label="Close menu">
            ×
          </button>
        </div>

        <div className="subMenuSearchWrap">
          <input
            ref={searchRef}
            type="search"
            className="subMenuSearch"
            placeholder="Jump to..."
            value={filterQuery}
            onChange={event => setFilterQuery(event.target.value)}
            aria-label={`Filter ${moduleLabel} pages`}
          />
        </div>

        <div className="subMenuItems">
          {filteredItems.length === 0 ? (
            <p className="subMenuEmpty">No matching pages</p>
          ) : (
            filteredItems.map((item, index) => {
              const previousGroup = filteredItems[index - 1]?.group;
              const showGroup = item.group && item.group !== previousGroup;
              const isActive = item.key === activeChildKey;

              return (
                <div key={item.key} className="subMenuEntry">
                  {showGroup ? <span className="subMenuGroup">{item.group}</span> : null}
                  <Link
                    to={item.url}
                    className={isActive ? "subMenuItem activeSubItem" : "subMenuItem"}
                    onClick={handleClose}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {item.name}
                  </Link>
                </div>
              );
            })
          )}
        </div>
      </SubMenuNav>
    </PopupModal>
  );
}

const slideIn = `
  @keyframes subMenuSlideIn {
    from {
      transform: translateX(-8px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const SubMenuNav = styled.nav`
  display: flex;
  position: fixed;
  left: ${MAIN_MENU_WIDTH}px;
  z-index: 20;
  height: 100%;
  width: ${SUB_MENU_WIDTH}px;
  padding: 0;
  flex-direction: column;
  flex-shrink: 0;
  border-right: 1px solid #d5deea;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  animation: subMenuSlideIn 150ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${slideIn}

  > .subMenuHeader {
    display: flex;
    height: 72px;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px 0 20px;
    box-sizing: border-box;
    border-bottom: 1px solid #e9edf5;
  }

  > .subMenuHeader > .moduleTitle {
    color: #0e0e2c;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.2;
  }

  > .subMenuHeader > .closeButton {
    border: none;
    background: transparent;
    color: #6b7280;
    font-size: 24px;
    line-height: 1;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover,
    &:focus-visible {
      background: #f1f5fd;
      color: #2060ea;
      outline: none;
    }
  }

  > .subMenuSearchWrap {
    padding: 12px 16px 0;
    flex-shrink: 0;
  }

  > .subMenuSearchWrap .subMenuSearch {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #d5deea;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13px;
    color: #262641;
    outline: none;

    &:focus {
      border-color: #2060ea;
      box-shadow: 0 0 0 2px rgba(32, 96, 234, 0.15);
    }

    &::placeholder {
      color: #9ca3af;
    }
  }

  > .subMenuItems {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px 12px 16px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;

    .subMenuEmpty {
      margin: 8px;
      color: #6b7280;
      font-size: 13px;
    }

    .subMenuItem {
      color: #262641;
      min-height: 36px;
      padding: 8px 10px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      gap: 6px;
      align-items: center;
      text-decoration: none;
      transition: background 0.15s ease, color 0.15s ease;
      border-left: 3px solid transparent;

      &:hover {
        background: #f1f5fd;
      }

      &:focus-visible {
        outline: 2px solid #2060ea;
        outline-offset: 1px;
      }

      &.activeSubItem {
        background: #e8effd;
        color: #2060ea;
        font-weight: 600;
        border-left-color: #2060ea;
      }
    }

    .subMenuEntry {
      display: flex;
      flex-direction: column;
    }

    .subMenuGroup {
      color: #6b7280;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.06em;
      line-height: 1.2;
      padding: 10px 10px 4px;
      text-transform: uppercase;
    }
  }
`;
